



















































































































































import Vue from 'vue';
import { Data as ArticleData } from '@root/modules/article/types/article';
import { Headlines as ArticleHeadlines } from '@root/modules/article/types/article';
import { DataWithHref, Data as HeadlinesData } from '@root/modules/headlines/types/headlines';
import getHeadlineWithHref from '@root/modules/headlines/utils/getHeadlineWithHref';
import ArticleHeadline from './ArticleHeadline.vue';
import Swiper from '@core/components/UI/swiper/Swiper.vue';
import UniversalHeadlinesService from '@root/modules/headlines/services/UniversalHeadlines.service';
import ArticleCxenseService from '@root/modules/article/services/ArticleCxense.service';
import getArticleUrl from '@root/modules/article/utils/buildArticleUrl';
import { gridConfig } from '@core/config/grid.config';

interface Methods {
  addClickData: (headline: HeadlinesData, params: Record<string, any>) => Record<string, any>;
  getHeadline: (headline: HeadlinesData) => DataWithHref;
  getCxenseHeadline: (headline: HeadlinesData) => DataWithHref;
  getCxenseHeadlines: () => void;
  getMixHeadlines: () => Promise<HeadlinesData[]>;
  getArticleHeadlines: (relatedHeadlineIds: number[], limit?: number) => Promise<HeadlinesData[]>;
  toggleTab: (section: string) => void;
  swiperOptions: (container: string) => Record<string, any>;
}

interface Computed {
  headlineList: DataWithHref[];
  ptArticles: number[];
  isCrArticle: boolean;
  crIndex: number | null;
}

interface Props {
  article: ArticleData;
  headlines: ArticleHeadlines;
  limit: number;
  start: number;
  tabbed: boolean;
  recommendedLimit: number;
  recommendedStart: number;
  betweenArticles: boolean;
  dataSource: string;
}

interface Data {
  domains: string | string[];
  locale: string;
  cXenseHeadlinesLimit: number;
  cXenseHeadlines: HeadlinesData[];
  cXenseArticles: {
    id: string;
    clickUrl: string;
  }[];
  mixHeadlines: HeadlinesData[];
  ptHeadlines: HeadlinesData[];
  isMobileView: boolean;
}

export default Vue.extend<Data, Methods, Computed, Props>({
  components: {
    ArticleHeadline,
    Swiper,
  },
  props: {
    article: {
      type: Object,
      required: false,
      default: null,
    },
    headlines: {
      type: Array,
      required: false,
      default: null,
    },
    limit: {
      type: Number,
      required: false,
      default: 6,
    },
    start: {
      type: Number,
      required: false,
      default: 0,
    },
    tabbed: {
      type: Boolean,
      required: false,
      default: false,
    },
    recommendedLimit: {
      type: Number,
      required: false,
      default: 6,
    },
    recommendedStart: {
      type: Number,
      required: false,
      default: 0,
    },
    betweenArticles: {
      type: Boolean,
      required: false,
      default: false,
    },
    dataSource: {
      type: String,
      required: false,
      default: 'cxense',
    },
  },
  data() {
    const { domain, locale, temporaryAllowedDomains } = this.$channelConfig('settings');
    const domains = temporaryAllowedDomains ? [...new Set([...temporaryAllowedDomains, domain])] : domain;
    return {
      domains,
      locale,
      cXenseHeadlines: [],
      cXenseHeadlinesLimit: 20,
      cXenseArticles: [],
      mixHeadlines: [],
      ptHeadlines: [],
      isMobileView: false,
    };
  },
  computed: {
    headlineList() {
      const limit = this.isMobileView ? 3 : this.limit;
      const headlines = (this.headlines && this.headlines.slice(this.start, limit).map(headline => this.getHeadline(headline))) || [];

      return headlines;
    },
    ptArticles() {
      const articleExtra = this.$store.getters['article/getArticleExtraContent'];
      const bottom = articleExtra && articleExtra.pt_bottom ? articleExtra.pt_bottom : null;
      const middle = articleExtra && articleExtra.pt_middle ? articleExtra.pt_middle : null;
      return middle ? bottom.concat(middle) : bottom || null;
    },
    isCrArticle() {
      const articles = this.$store.getters['article/getCrArticles'];

      return articles.find((article: number) => article === this.article.id);
    },
    crIndex() {
      if (this.isCrArticle) {
        const id = this.article.id;
        const crArticles = this.$store.state.article.crArticles;
        return crArticles.indexOf(id) + 1;
      }

      return null;
    },
  },
  watch: {
    ptArticles: {
      async handler(articleExtra) {
        if (articleExtra) {
          const limit = this.isMobileView ? 3 : 0;
          this.ptHeadlines = await this.getArticleHeadlines(articleExtra, limit);
          this.mixHeadlines = await this.getMixHeadlines();
        }
      },
      immediate: true,
    },
  },
  mounted() {
    if (process.client) {
      const screenWidth = window.screen.width;
      this.isMobileView = screenWidth <= gridConfig.breakpoints.lg;
    } else {
      this.isMobileView = this.$ua && this.$ua.deviceType() === 'smartphone' && !this.$ua.isFromTablet();
    }

    this.$nextTick(() => {
      if (this.dataSource === 'cxense') {
        this.getCxenseHeadlines();
      }
    });
  },
  methods: {
    addClickData(headline: HeadlinesData, params: Record<string, any>) {
      const clickData: Record<string, any> = {};

      clickData.articleLayout = 'b';
      clickData.listType = params.listType;
      clickData.listPosition = params.listPosition;
      clickData.listArticleId = headline.id;
      clickData.listArticleUrl = getArticleUrl({ article: headline, domain: headline.primaryCategory.channel.url });
      clickData.listArticlePosition = params.listIndex + 1;

      return { ...headline, clickData };
    },
    getHeadline(headline) {
      const fakeDomainEnabled = this.$config.FAKE_DOMAIN_ENABLED;
      return getHeadlineWithHref({ headline, domain: this.domains, fakeDomainEnabled });
    },
    getCxenseHeadline(headline) {
      const cXenseArticle = this.cXenseArticles.find(article => article.id === String(headline.id));
      return cXenseArticle ? { ...headline, href: cXenseArticle?.clickUrl } : this.getHeadline(headline);
    },
    async getMixHeadlines() {
      const mixHeadlines: HeadlinesData[] = [];
      const topHeadlinesList = this.$channelConfig('page').article.component.topHeadlines.globalTopHeadlinesIds || [];
      let ptHeadlines = this.ptHeadlines || [];
      let topHeadlines = topHeadlinesList.length ? await this.getArticleHeadlines(topHeadlinesList) : [];

      let topHeadlinesLimit = 6;
      let topHeadlinesStart = 0;
      let topHeadlinesEnd = topHeadlinesStart + topHeadlinesLimit;

      let ptHeadlinesLimit = 3;
      let ptHeadlinesStart = 0;
      let ptHeadlinesEnd = ptHeadlinesStart + ptHeadlinesLimit;

      if (this.isCrArticle && this.crIndex) {
        topHeadlinesStart = this.crIndex * topHeadlinesLimit;
        if (topHeadlinesStart + topHeadlinesLimit > topHeadlines.length) {
          topHeadlinesStart = topHeadlines.length - topHeadlinesLimit;
        }

        topHeadlinesEnd = topHeadlinesStart + topHeadlinesLimit;
        if (topHeadlinesEnd > topHeadlines.length) {
          topHeadlinesEnd = topHeadlines.length;
        }

        ptHeadlinesStart = this.crIndex * ptHeadlinesLimit;
        if (ptHeadlinesStart + ptHeadlinesLimit > ptHeadlines.length) {
          ptHeadlinesStart = ptHeadlines.length - ptHeadlinesLimit;
        }

        ptHeadlinesEnd = ptHeadlinesStart + ptHeadlinesLimit;
        if (ptHeadlinesEnd > ptHeadlines.length) {
          ptHeadlinesEnd = ptHeadlines.length;
        }
      }

      topHeadlines = topHeadlines.slice(topHeadlinesStart, topHeadlinesEnd);
      ptHeadlines = ptHeadlines.slice(ptHeadlinesStart, ptHeadlinesEnd);

      mixHeadlines[0] = topHeadlines[0];
      mixHeadlines[1] = topHeadlines[1];
      mixHeadlines[2] = ptHeadlines[0] || topHeadlines[2];
      mixHeadlines[3] = ptHeadlines[0] ? topHeadlines[2] : topHeadlines[3];
      mixHeadlines[4] = ptHeadlines[0] ? topHeadlines[3] : topHeadlines[4];

      if (ptHeadlines[1] || topHeadlines[5]) {
        mixHeadlines[5] = ptHeadlines[1] || topHeadlines[5];
      }

      return mixHeadlines;
    },
    async getCxenseHeadlines() {
      const widgetId = this.$channelConfig('page').article.component.cXense?.widgetId;

      if (!widgetId) {
        return false;
      }

      // Get cXense page context for more relevant data
      const articleCxenseService = new ArticleCxenseService();
      this.cXenseArticles = await articleCxenseService.fetch({ widgetId });

      const limit = this.isMobileView ? 3 : this.recommendedLimit;

      let start = this.isCrArticle && this.crIndex ? this.recommendedStart * this.crIndex + this.recommendedStart + limit : this.recommendedStart;
      let end = start + limit;

      if (start > this.cXenseArticles.length) {
        start = this.cXenseArticles.length - limit;
      }

      if (end > this.cXenseArticles.length) {
        end = this.cXenseArticles.length;
      }

      const cXenseArticles = this.cXenseArticles.slice(start, end);
      if (cXenseArticles.length) {
        this.cXenseHeadlines = await this.getArticleHeadlines(
          cXenseArticles.map(article => Number(article.id)),
          limit
        );
      }
    },
    async getArticleHeadlines(relatedHeadlineIds, limit = 0) {
      const universalHeadlinesService = new UniversalHeadlinesService();
      // FIX ME: Removed language: this.language variable as content-api-v3 has now channel.language: "ET" imported;
      const headlines = await universalHeadlinesService.fetch({ id: relatedHeadlineIds, limit });
      return headlines.items;
    },
    toggleTab(section) {
      const id = this.$props.article.id;
      const container = window.document.querySelector(`[data-headlines-section="${id}"]`) as HTMLElement;
      const targetNavigationItem = container.querySelector(`.C-article-headlines__tabs-navigation--item-${section}`) as HTMLElement;
      const targetContainer = container.querySelector(`.C-article-headlines__tabs-content--tab-${section}`) as HTMLElement;
      const targetContainerIsActive = targetContainer.classList.contains('C-article-headlines__tabs-content--tab-active');

      const navigationItems = container.querySelectorAll('.C-article-headlines__tabs-navigation--item');
      navigationItems.forEach(element => {
        const navigationItem = element as HTMLElement;
        navigationItem.classList.remove('C-article-headlines__tabs-navigation--item-active');
      });

      const tabs = container.querySelectorAll('.C-article-headlines__tabs-content--tab');
      tabs.forEach(element => {
        const tab = element as HTMLElement;
        tab.classList.remove('C-article-headlines__tabs-content--tab-active');
      });

      if (!targetContainerIsActive) {
        targetNavigationItem.classList.toggle('C-article-headlines__tabs-navigation--item-active');
        targetContainer.classList.toggle('C-article-headlines__tabs-content--tab-active');
      }
    },
    swiperOptions(container: string) {
      let swiperOptions = {
        spaceBetween: 12,
        initialSlide: 0,
        loop: false,
        autoplay: false,
        freeMode: {
          enabled: true,
          sticky: true,
        },
        slidesPerView: 1.2,
        slidesPerGroup: 1.2,
        pagination: {
          enabled: false,
        },
        navigation: {
          nextEl: `.${container} .swiper-button-next`,
          prevEl: `.${container} .swiper-button-prev`,
          enabled: false,
        },
        scrollbar: {
          enabled: false,
        },
        breakpoints: {
          768: {
            freeMode: false,
            slidesPerView: 3,
            slidesPerGroup: 3,
            loop: false,
            navigation: {
              enabled: true,
            },
          },
        },
      };

      return swiperOptions;
    },
  },
});
